import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { supabase, dbHelpers } from '../lib/supabase'
import { runCompatibilityCheck, fixCompatibilityIssues } from '../utils/browserCompatibility'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const refreshTimerRef = useRef(null)

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
  }

  const scheduleTokenRefresh = (session) => {
    try {
      clearRefreshTimer()
      const expiresAtSec = session?.expires_at
      if (!expiresAtSec) return
      const nowMs = Date.now()
      const expiresMs = expiresAtSec * 1000
      // Refresh 60s before expiry, with a minimum delay
      const refreshDelay = Math.max(expiresMs - nowMs - 60_000, 15_000)
      refreshTimerRef.current = setTimeout(async () => {
        try {
          await refreshSession()
        } catch (err) {
          console.warn('Scheduled token refresh failed:', err)
        }
      }, refreshDelay)
    } catch (err) {
      console.warn('Failed to schedule token refresh:', err)
    }
  }

  useEffect(() => {
    // Simplified browser compatibility check to prevent loading delays
    const checkBrowserCompatibility = async () => {
      try {
        // Quick basic checks only
        const hasLocalStorage = typeof(Storage) !== "undefined" && localStorage
        const hasCookies = navigator.cookieEnabled
        const isIE = /MSIE|Trident/.test(navigator.userAgent)
        
        if (isIE) {
          setError('Internet Explorer is not supported. Please use a modern browser like Chrome, Firefox, or Edge.')
          return false
        }
        
        if (!hasLocalStorage) {
          setError('localStorage is required for authentication. Please enable it in your browser settings.')
          return false
        }
        
        if (!hasCookies) {
          console.warn('Cookies are disabled. Some features may not work properly.')
        }
        
        return true
      } catch (error) {
        console.error('Basic compatibility check failed:', error)
        // Don't block authentication for minor compatibility issues
        return true
      }
    }

    // Get initial session with enhanced error handling
    const getInitialSession = async () => {
      try {
        // Check browser compatibility first (non-blocking)
        await checkBrowserCompatibility()
        
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          // Handle specific refresh token errors
          if (error.message?.includes('refresh_token_not_found') || 
              error.message?.includes('Invalid Refresh Token') ||
              error.message?.includes('JWT expired') ||
              error.message?.includes('invalid_grant')) {
            console.warn('Auth token invalid, clearing session:', error.message)
            await clearCorruptedSession()
            setError('Session expired. Please sign in again.')
            return
          }
          throw error
        }
        
        if (session?.user) {
          // Validate session integrity
          if (session.access_token && session.refresh_token) {
            setUser(session.user)
            await loadUserProfile(session.user.id)
            scheduleTokenRefresh(session)
          } else {
            console.warn('Incomplete session data, clearing session')
            await clearCorruptedSession()
            setError('Incomplete session data. Please sign in again.')
          }
        }
      } catch (error) {
        console.error('Error getting initial session:', error)
        setError(error.message)
        // Clear potentially corrupted session data
        await clearCorruptedSession()
      } finally {
        setLoading(false)
      }
    }

    getInitialSession()

    // Listen for auth changes with enhanced error handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.id)
        
        try {
          if (event === 'SIGNED_OUT' || !session) {
            setUser(null)
            setProfile(null)
            setError(null)
            clearRefreshTimer()
          } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            if (session?.user) {
              setUser(session.user)
              await loadUserProfile(session.user.id)
              setError(null) // Clear any previous errors
              scheduleTokenRefresh(session)
            }
          } else if (event === 'USER_UPDATED') {
            if (session?.user) {
              setUser(session.user)
              // Optionally reload profile if user data changed
            }
          }
        } catch (error) {
          console.error('Error handling auth state change:', error)
          if (error.message?.includes('refresh_token_not_found') || 
              error.message?.includes('Invalid Refresh Token')) {
            console.warn('Refresh token error during state change, signing out')
            await supabase.auth.signOut()
          }
        } finally {
          setLoading(false)
        }
      }
    )

    // Activity-based session checks to prevent stale auth after idle
    const ensureFreshSession = async () => {
      try {
        const status = await getSessionStatus()
        if (!status.hasSession || !status.hasAccessToken) {
          await refreshSession()
          return
        }
        const nowSec = Math.floor(Date.now() / 1000)
        const secondsLeft = (status.expiresAt || nowSec) - nowSec
        if (secondsLeft < 90) {
          await refreshSession()
        }
      } catch (err) {
        console.warn('ensureFreshSession error:', err)
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') ensureFreshSession()
    }
    const onFocus = () => ensureFreshSession()
    const onOnline = () => ensureFreshSession()

    window.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    window.addEventListener('online', onOnline)

    return () => {
      subscription.unsubscribe()
      window.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('online', onOnline)
      clearRefreshTimer()
    }
  }, [])

  const loadUserProfile = async (userId) => {
    try {
      const userProfile = await dbHelpers.getUserProfile(userId)
      setProfile(userProfile)
    } catch (error) {
      // If profile doesn't exist by ID, try to find by email
      if (error.code === 'PGRST116') {
        console.log('User profile not found by ID, checking by email...')
        try {
          // Get user info from auth
          const { data: { user } } = await supabase.auth.getUser()
          if (user) {
            // Try to find existing profile by email
            const { data: existingProfile, error: emailError } = await supabase
              .from('users')
              .select('*')
              .eq('email', user.email)
              .single()
            
            if (existingProfile && !emailError) {
              console.log('Found existing profile by email, updating ID...')
              // Update the profile with correct user ID
              const { data: updatedProfile, error: updateError } = await supabase
                .from('users')
                .update({ id: userId, updated_at: new Date().toISOString() })
                .eq('email', user.email)
                .select()
                .single()
              
              if (updatedProfile && !updateError) {
                setProfile(updatedProfile)
                console.log('Profile ID updated successfully')
                return
              }
            }
            
            // Only create new profile if none exists
            console.log('No existing profile found, creating basic profile...')
            
            // ENHANCED: Try to get name from multiple sources with better formatting
            const nameFromMetadata = user.user_metadata?.name?.trim()
            const nameFromEmail = user.email.split('@')[0]
            
            // ENHANCED: Format the name properly (capitalize first letters)
            let displayName = nameFromMetadata && nameFromMetadata !== '' ? nameFromMetadata : nameFromEmail
            
            // Format name if it looks like an email prefix or username
            if (displayName.includes('.') || displayName.includes('_') || displayName.includes('-')) {
              // Split by common separators and capitalize each part
              const nameParts = displayName.split(/[._-]/)
              displayName = nameParts.map(part => 
                part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
              ).join(' ')
            } else if (displayName.length > 0) {
              // Simple capitalization for single words
              displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1).toLowerCase()
            }
            
            // ENHANCED: Get user type with better default
            const userType = user.user_metadata?.user_type || 'government_employee'
            
            const basicProfileData = {
              email: user.email,
              name: displayName,
              agency: user.user_metadata?.agency || 'Not specified',
              position: user.user_metadata?.position || 'Not specified',
              user_type: userType,
              phone: user.user_metadata?.phone || '',
              department: user.user_metadata?.department || ''
            }
            
            const newProfile = await dbHelpers.createUserProfile(userId, basicProfileData)
            setProfile(newProfile)
            console.log('Basic user profile created successfully with formatted name:', displayName)
          }
        } catch (createError) {
          console.error('Error handling user profile:', createError)
        }
      } else {
        console.error('Error loading user profile:', error)
      }
    }
  }

  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true)
      setError(null)
      
      // ENHANCED: Format name properly before signup
      let formattedName = userData.name?.trim() || email.split('@')[0]
      
      // Apply proper name formatting
      if (formattedName.includes('.') || formattedName.includes('_') || formattedName.includes('-')) {
        const nameParts = formattedName.split(/[._-]/)
        formattedName = nameParts.map(part => 
          part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        ).join(' ')
      } else if (formattedName.length > 0) {
        formattedName = formattedName.charAt(0).toUpperCase() + formattedName.slice(1).toLowerCase()
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...userData,
            name: formattedName, // Use properly formatted name
            agency: userData.agency || '',
            position: userData.position || '',
            phone: userData.phone || '',
            department: userData.department || '',
            role: userData.role || 'IT Officer'
          }
        }
      })
      
      if (error) throw error
      
      // Create user profile if signup successful
      if (data.user) {
        try {
          const profileData = {
            email: data.user.email,
            name: formattedName, // Use the properly formatted name
            agency: userData.agency || '',
            position: userData.position || '',
            user_type: userData.user_type || 'government_employee', // Default to government_employee
            phone: userData.phone || '',
            department: userData.department || '',
            role: userData.role || 'IT Officer',
            approval_status: userData.approval_status || 'pending'
          }
          
          const newProfile = await dbHelpers.createUserProfile(data.user.id, profileData)
          console.log('User profile created successfully during signup:', newProfile)
          
          // Store the formatted name in user metadata for immediate use
          if (data.user.user_metadata) {
            data.user.user_metadata.name = formattedName
          }
          
        } catch (profileError) {
          console.error('Error creating user profile during signup:', profileError)
          // Don't throw here as the user account was created successfully
          // The profile will be created later when they sign in
        }
      }
      
      return { data, error: null }
    } catch (error) {
      setError(error.message)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) throw error
      
      return { data, error: null }
    } catch (error) {
      setError(error.message)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (updates) => {
    try {
      setLoading(true)
      setError(null)
      
      if (!user) throw new Error('No user logged in')
      
      // ENHANCED: Handle name formatting if name is being updated
      if (updates.name) {
        let formattedName = updates.name.trim()
        
        // Format name if it looks like an email prefix or username
        if (formattedName.includes('.') || formattedName.includes('_') || formattedName.includes('-')) {
          const nameParts = formattedName.split(/[._-]/)
          formattedName = nameParts.map(part => 
            part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
          ).join(' ')
        } else if (formattedName.length > 0) {
          formattedName = formattedName.charAt(0).toUpperCase() + formattedName.slice(1).toLowerCase()
        }
        
        updates.name = formattedName
      }
      
      // ENHANCED: Ensure user_type has proper default
      if (!updates.user_type && profile?.user_type) {
        updates.user_type = profile.user_type
      } else if (!updates.user_type) {
        updates.user_type = 'government_employee'
      }
      
      const updatedProfile = await dbHelpers.updateUserProfile(user.id, updates)
      setProfile(updatedProfile)
      
      return { data: updatedProfile, error: null }
    } catch (error) {
      setError(error.message)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email) => {
    try {
      setLoading(true)
      setError(null)
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })
      
      if (error) throw error
      
      return { error: null }
    } catch (error) {
      setError(error.message)
      return { error }
    } finally {
      setLoading(false)
    }
  }

  // Enhanced session cleanup for corrupted data
  const clearCorruptedSession = async () => {
    try {
      // Clear all possible storage locations
      const storageKeys = [
        'cybergov-auth-token',
        'sb-uaooghxtslvyaizttavi-auth-token',
        'supabase.auth.token',
        'sb-auth-token'
      ]
      
      // Clear from localStorage
      storageKeys.forEach(key => {
        try {
          localStorage.removeItem(key)
        } catch (error) {
          console.warn(`Failed to remove ${key} from localStorage:`, error)
        }
      })
      
      // Clear from sessionStorage
      storageKeys.forEach(key => {
        try {
          sessionStorage.removeItem(key)
        } catch (error) {
          console.warn(`Failed to remove ${key} from sessionStorage:`, error)
        }
      })
      
      // Clear memory storage
      if (window._cybergovMemoryStorage) {
        storageKeys.forEach(key => {
          delete window._cybergovMemoryStorage[key]
        })
      }
      
      // Sign out from Supabase
      await supabase.auth.signOut()
      
      // Reset state
      setUser(null)
      setProfile(null)
      
      console.log('Corrupted session data cleared successfully')
      return { success: true }
    } catch (error) {
      console.error('Error clearing corrupted session data:', error)
      return { success: false, error: error.message }
    }
  }

  // Session recovery and debugging utilities
  const clearSessionData = async () => {
    return await clearCorruptedSession()
  }

  const refreshSession = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase.auth.refreshSession()
      
      if (error) {
        console.warn('Session refresh failed:', error.message)
        if (error.message?.includes('refresh_token_not_found') || 
            error.message?.includes('Invalid Refresh Token')) {
          await clearSessionData()
          setError('Session expired. Please sign in again.')
        }
        throw error
      }
      
      if (data.session?.user) {
        setUser(data.session.user)
        await loadUserProfile(data.session.user.id)
        setError(null)
        console.log('Session refreshed successfully')
      }
      
      return { success: true, session: data.session }
    } catch (error) {
      console.error('Error refreshing session:', error)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  const getSessionStatus = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      return {
        hasSession: !!session,
        hasUser: !!session?.user,
        hasRefreshToken: !!session?.refresh_token,
        hasAccessToken: !!session?.access_token,
        expiresAt: session?.expires_at,
        error: error?.message
      }
    } catch (error) {
      return {
        hasSession: false,
        error: error.message
      }
    }
  }

  // Make debugging functions available globally in development
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    window.authDebug = {
      clearSessionData,
      refreshSession,
      getSessionStatus,
      getCurrentUser: () => user,
      getCurrentProfile: () => profile,
      checkCompatibility: async () => {
        const result = await runCompatibilityCheck()
        console.log('Browser compatibility check:', result)
        return result
      },
      fixCompatibility: async () => {
        const result = await fixCompatibilityIssues()
        console.log('Compatibility fix result:', result)
        return result
      }
    }
  }

  const value = {
    user,
    profile,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
    clearSessionData,
    refreshSession,
    getSessionStatus,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext